/**
 * A certificate's public page, at `/c/<id>`.
 *
 * ## Who this is for
 *
 * The reader of components/profile/PublicProfile.tsx, one step further along:
 * somebody deciding about a person — an employer, a recruiter — who was handed
 * an id or a link off a CV and has opened it to find out whether it is real.
 * They have never heard of this product and have been trained by a decade of
 * completion certificates to discount exactly this kind of page. So the page
 * answers their questions in the order they ask them:
 *
 *   1. **Is it valid?** First, in two words, before the name of anything.
 *   2. **What is it, and whose?** The certificate's name, the holder, the date.
 *   3. **What does it rest on?** The claim sentence, then the checkpoints —
 *      each by the world it covers, which is a subject a stranger can hold,
 *      and the date a server passed it.
 *   4. **How far does that go?** Three facts about the machinery, one of which
 *      is where it stops, and who is vouching.
 *
 * What it may say is fixed by docs/certificates.md, "What it may claim", and
 * strings/certificatePage.ts is written to it: nothing here says anything
 * about the holder the grader did not measure.
 *
 * ## A withdrawn certificate is still a page
 *
 * A link somebody already shared has to go on answering, and it has to stop
 * vouching (docs/certificates.md, "Revocation"). So a revoked certificate
 * keeps its name, holder, dates and id, says since when it is no longer valid,
 * and drops the claim and the checkpoint list: a list of passes under a
 * heading that says "the checkpoints behind it" is still vouching, whatever
 * the badge above it says.
 *
 * ## Why it is its own entry
 *
 * For PublicProfile.tsx's reason. App boots a virtual machine, and a stranger
 * checking one certificate should not download a hundred megabytes to do it,
 * so main.tsx mounts this instead of App when the path names a certificate.
 * One request to one public route, and a page.
 */
import { useEffect, useState } from 'react';
import { badgeForMission } from '@engineer-os/engine';
import type { PublicCertificateProps } from '../contracts';
import { fetchCertificate, type CertificateCheckpoint, type PublicCertificate as CertificateRecord } from '../../lib/publicCertificate';
import { personPath } from '../../lib/person';
import { calendarDate } from '../../lib/format';
import { useLocale, useStrings } from '../../state/useLocale';
import { Brand, LinkButton, Machine } from '../../ui';
import { Corner } from '../Corner';

type Strings = ReturnType<typeof useStrings>;

/**
 * The world a checkpoint stands over, in the reader's language.
 *
 * Through the badge table rather than a second mapping: `badgeForMission` is
 * the one place a checkpoint is tied to its world, and `home.worlds` is where
 * a world's name is already translated for the map (docs/i18n.md §6.1).
 * PublicProfile.tsx names worlds the same way.
 *
 * A checkpoint this bundle cannot name falls back to its own id rather than
 * being dropped, which is the opposite of what the profile does and on
 * purpose. The profile lists what is confirmed now and can lose a row it has
 * no word for; a certificate is a frozen record of a fixed set, and a list one
 * short of the set would be the page misreporting what was issued.
 */
function checkpointName(s: Strings, missionId: string): string | null {
  const world = badgeForMission(missionId)?.world;
  if (!world) return null;
  const worlds = s.home.worlds as { [world: string]: { name: string } | undefined };
  return worlds[world]?.name ?? null;
}

/**
 * The screen, and the one request behind it.
 *
 * Split from the page for PublicProfile.tsx's reason: `renderToStaticMarkup`
 * runs no effects, so only a component that takes the record in hand can be
 * rendered to markup and looked at in both themes and both languages.
 */
export function PublicCertificate({ id }: PublicCertificateProps) {
  const s = useStrings();
  /** Absent while asking, null once it is known there is no such certificate. */
  const [record, setRecord] = useState<CertificateRecord | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void fetchCertificate(id).then((r) => {
      if (live) setRecord(r);
    });
    return () => {
      live = false;
    };
  }, [id]);

  // The tab's title says the state as well as the name. A reader who keeps
  // several candidates' links open in tabs should be able to see which one
  // was withdrawn without opening it again. Open Graph would need the
  // document assembled per id in the Worker, which needs `/c/*` in
  // `run_worker_first` (wrangler.toml); until then a pasted link gets the
  // site-wide card in index.html, as `/u/<tag>` does.
  useEffect(() => {
    if (record === undefined) return;
    if (record === null) {
      document.title = s.certificatePage.tabMissing;
      return;
    }
    const name = s.certificate.names[record.certificate];
    document.title = record.valid
      ? s.certificatePage.tab(record.holder, name)
      : s.certificatePage.tabRevoked(record.holder, name);
  }, [record, s]);

  return <CertificatePage id={id} record={record} />;
}

/**
 * The page itself: a certificate, a withdrawn one, or none.
 *
 * `id` is the one from the address, kept for the not-found state so the
 * reader can compare what they opened with what they were given — a
 * transposed pair of characters is the likeliest reason for a miss.
 */
export function CertificatePage({ id, record }: { id: string; record: CertificateRecord | null | undefined }) {
  const s = useStrings();
  const c = s.certificatePage;
  const { locale } = useLocale();
  const date = (ms: number) => calendarDate(new Date(ms), locale);

  return (
    <div className="profile person certificate">
      {/* The corner every page has, chat door included (components/Corner.tsx). */}
      <Corner />
      <header className="profile-top">
        {/* `/`, not `?`, for the reason PublicProfile.tsx gives: this page
            does not live in the query string. */}
        <Brand compact href="/" />
      </header>

      <main className="profile-page">
        {record === undefined ? (
          <p className="note" role="status">
            {c.loading}
          </p>
        ) : record === null ? (
          <section className="person-missing">
            <h1 className="profile-name">{c.missing.title}</h1>
            <p className="note">{c.missing.body}</p>
            <div className="certificate-id">
              <p className="label">{c.missing.asked}</p>
              <Machine className="certificate-id-value">{id}</Machine>
            </div>
            <p className="person-cta">
              <LinkButton variant="solid" href="/">
                {c.cta}
              </LinkButton>
            </p>
          </section>
        ) : (
          <>
            <section className="certificate-head">
              {/* The answer to the question the reader came with, above the
                  name of anything. A state, not a seal: an acid square for
                  valid — the mark the public profile gives a confirmed world
                  — and a hollow one for withdrawn, so the difference survives
                  a screen with no colour in it. */}
              <p className={record.valid ? 'certificate-state is-valid' : 'certificate-state is-revoked'} role="status">
                <span className="certificate-state-mark" aria-hidden="true" />
                <strong className="certificate-state-word">
                  {record.valid
                    ? c.valid
                    : record.revokedAt !== null
                      ? c.revoked.since(date(record.revokedAt))
                      : c.revoked.state}
                </strong>
              </p>

              <p className="label">{c.label}</p>
              <h1 className="profile-name certificate-name">{s.certificate.names[record.certificate]}</h1>

              <dl className="certificate-facts">
                <div className="certificate-fact">
                  <dt className="label">{c.issuedTo}</dt>
                  {/* The name is the holder's own and may be in either
                      script, so it takes its direction from itself
                      (docs/i18n.md §7). Sized to its content, so a Latin name
                      on an Arabic page still sits on the reading edge. */}
                  <dd className="certificate-holder">
                    <bdi>{record.holder}</bdi>
                  </dd>
                </div>
                <div className="certificate-fact">
                  <dt className="label">{c.issued}</dt>
                  <dd className="certificate-date">{date(record.issuedAt)}</dd>
                </div>
              </dl>

              <p className={record.valid ? 'person-summary' : 'person-summary certificate-withdrawn'}>
                {record.valid ? c.validBody : c.revoked.body}
              </p>
            </section>

            {record.valid && (
              <section className="certificate-proof" aria-label={c.checkpoints.label}>
                <h2 className="profile-h2">{c.checkpoints.label}</h2>
                {/* The claim, verbatim, above the list it is a claim about.
                    The one sentence a certificate may say, from the catalogue
                    it is shared through, so it cannot drift from what the
                    course says when the certificate is earned. */}
                <p className="certificate-claim">{s.certificate.claim}</p>
                <ul className="person-world-list certificate-checkpoints">
                  {record.evidence.map((e) => (
                    <Checkpoint key={e.missionId} s={s} e={e} date={date} />
                  ))}
                </ul>

                {/* The argument, as three terms — PublicProfile.tsx's shape,
                    and for its reason: a reader who skims three bold phrases
                    has still received it. The third is the limit. */}
                <h3 className="label person-how-label">{c.how.label}</h3>
                <dl className="person-how">
                  {[c.how.server, c.how.own, c.how.limit].map((item) => (
                    <div className="person-how-item" key={item.term}>
                      <dt className="person-how-term">{item.term}</dt>
                      <dd className="person-how-body note">{item.body}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            <section className="certificate-refs">
              <div className="certificate-id">
                <p className="label">{c.id.label}</p>
                {/* Machine text, and selectable as one piece: it is read back
                    down a phone and pasted into an applicant tracker, and a
                    reader who has to drag-select across hyphens will miss a
                    group. Left to right in both languages, like every id. */}
                <Machine className="certificate-id-value">{record.id}</Machine>
                <p className="note">{c.id.body}</p>
              </div>
              {record.tag && (
                <div className="certificate-id">
                  <p className="label">{c.profile.label}</p>
                  <a className="certificate-link" href={personPath(record.tag)}>
                    {c.profile.link}
                  </a>
                  <p className="note">{c.profile.body}</p>
                </div>
              )}
              <p className="note">{c.provenance}</p>
            </section>

            <section className="person-cta-band">
              <p className="note">{c.ctaWhy}</p>
              <LinkButton variant="solid" href="/">
                {c.cta}
              </LinkButton>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

/** One checkpoint behind a valid certificate: the world it covers, and when it passed. */
function Checkpoint({ s, e, date }: { s: Strings; e: CertificateCheckpoint; date: (ms: number) => string }) {
  const c = s.certificatePage.checkpoints;
  const name = checkpointName(s, e.missionId);
  return (
    <li className="person-world">
      <span className="person-world-mark" aria-hidden="true" />
      <span className="person-world-copy">
        {/* No `dir="auto"`: a world's name is this catalogue's own copy in
            the page's language, and one that opens on a Latin word — `Git في
            الفريق` — would otherwise be set left to right and parked on the
            wrong edge of an Arabic row. */}
        {name !== null ? (
          <strong className="person-world-name">{name}</strong>
        ) : (
          <strong className="person-world-name">
            <Machine>{e.missionId}</Machine>
          </strong>
        )}
        <span className="note">
          {c.on(date(e.verifiedAt))}
          {/* The grading contract that pass was accepted under. A pass at an
              older version counts (docs/certificates.md, "What counts as a
              pass"), and saying which one is how that is not hidden. Quiet,
              because it is for the one reader in a hundred who asks. */}
          {e.version !== '' && (
            <>
              {' · '}
              <span className="certificate-version">
                {c.graded} <Machine>{e.version}</Machine>
              </span>
            </>
          )}
        </span>
      </span>
    </li>
  );
}

export default PublicCertificate;
